import { getBookshelf, UserDataModel } from '@pm2/bookshelf-page';
import { pm2SitemapStore } from '@pm2/sitemap-utils';
import { pm2ConfigStore } from '@pm2/store';
import { makeTanstackQuery } from '@provider/tanstack';
import { useUserIdentityState } from '@provider/user-identity';
import { DAYS_TO_MS, HOURS_TO_MS } from '@utils/constants';

const getBookshelfFavouritesQuery = makeTanstackQuery({
  defaultQueryConfig: {
    staleTime: 24 * HOURS_TO_MS,
    gcTime: 21 * DAYS_TO_MS,
    meta: {
      persist: true,
      revalidateOnRefresh: true
    }
  },
  queryKey: input => ["user-area/bookshelf-favourites/get", input.portalId, input.user?.id],
  queryFn: async ({
    user,
    configStore,
    sitemapStore
  }) => {
    if (!user?.institution?.id) {
      return [];
    }
    const {
      config,
      previewMode
    } = configStore.getState();
    const shelfDescriptors = sitemapStore.getState().rootDescriptors.filter(it => it.type === "Shelf");
    const userDataModels = await Promise.all(shelfDescriptors.map(async it => {
      const shelf = await getBookshelf(config.id, it.id, {
        previewMode
      });
      return UserDataModel.FETCH(user.id, user.institution?.id ?? "", config.uniqueId, shelf);
    }));
    const books = await Promise.all(userDataModels.flatMap(userDataModel => userDataModel.getFavorites().map(async book => ({
      relatedShelfIds: [userDataModel.relatedShelfId],
      authors: book.authors,
      cover: book.cover,
      description: await book.getDescription(),
      id: book.id,
      isbn: book.isbn,
      letComplexity: book.letComplexity,
      lixComplexity: book.lixComplexity,
      title: book.title,
      url: book.url,
      badge: book.badge,
      badgeColor: book.badgeColor,
      category: book.category,
      grades: book.grades,
      highlighted: book.highlighted,
      levels: book.levels,
      relatedTask: book.relatedTask,
      seriesTitle: book.seriesTitle,
      subjects: book.subjects
    }))));
    const dedupedBooks = new Map();
    for (const book of books) {
      const dedupedBook = dedupedBooks.get(book.id);
      if (dedupedBook) {
        dedupedBook.relatedShelfIds.push(book.id);
      } else {
        dedupedBooks.set(book.id, book);
      }
    }
    return [...dedupedBooks.values()];
  },
  useQueryInput(queryFactory) {
    const sitemapStore = pm2SitemapStore.useStore();
    const configStore = pm2ConfigStore.useStore();
    const user = useUserIdentityState(state => state.user);
    const portalId = configStore.useSelector(state => state.config.id);
    return queryFactory({
      sitemapStore,
      configStore,
      user,
      portalId
    });
  }
});

export { getBookshelfFavouritesQuery };
//# sourceMappingURL=get-bookshelf-favourites.query.js.map
